/**
 * BookingStatusBlock — inserted into the Conversations right-side panel.
 * Surfaces the full booking lifecycle clearly:
 *   - Booking State (prominent badge)
 *   - Calendar Sync Status
 *   - Proposed Slot
 *   - Confirmed Slot
 *   - Calendar Event + provider
 *
 * Designed for demo clarity: every field has an explicit row and label.
 */
import React from 'react';
import { Badge } from './ui/badge';
import {
  Calendar, Eye, Zap, CheckCircle, AlertTriangle, Clock,
  ExternalLink, Cloud, CloudOff, CloudUpload, FlaskConical,
  Circle, ArrowRight,
} from 'lucide-react';

const STATE_META = {
  collecting:              { label: 'Collecting info',      color: 'bg-gray-100 text-gray-800 border-gray-200',         icon: Clock },
  clarifying:              { label: 'Clarifying',           color: 'bg-gray-100 text-gray-800 border-gray-200',         icon: Clock },
  slot_negotiation:        { label: 'Negotiating slot',     color: 'bg-blue-50  text-blue-800  border-blue-200',        icon: Clock },
  booking_ready:           { label: 'Ready to book',        color: 'bg-amber-50 text-amber-800 border-amber-200',       icon: Clock },
  awaiting_human_approval: { label: 'Awaiting approval',    color: 'bg-amber-50 text-amber-800 border-amber-200',       icon: Clock },
  booked:                  { label: 'Booked',               color: 'bg-emerald-50 text-emerald-800 border-emerald-200', icon: CheckCircle },
  calendar_sync_failed:    { label: 'Calendar sync failed', color: 'bg-red-50   text-red-800   border-red-200',         icon: AlertTriangle },
  cancel_requested:        { label: 'Cancel requested',     color: 'bg-red-50   text-red-800   border-red-200',         icon: AlertTriangle },
  reschedule_requested:    { label: 'Reschedule requested', color: 'bg-amber-50 text-amber-800 border-amber-200',       icon: Clock },
};

const SYNC_META = {
  synced:  { label: 'Synced',       color: 'bg-emerald-50 text-emerald-800 border-emerald-200', icon: Cloud },
  pending: { label: 'Pending sync', color: 'bg-amber-50  text-amber-800 border-amber-200',      icon: CloudUpload },
  failed:  { label: 'Sync failed',  color: 'bg-red-50    text-red-800    border-red-200',      icon: CloudOff },
};

const formatSlot = (slot) => {
  if (!slot) return null;
  const date =
    slot.date_label ||
    slot.normalized_date ||
    (slot.start_iso ? String(slot.start_iso).slice(0, 10) : '');
  const time =
    slot.time_label ||
    slot.normalized_time ||
    (slot.start_iso ? String(slot.start_iso).slice(11, 16) : '');
  if (!date && !time) return null;
  return `${date}${date && time ? ' · ' : ''}${time}`;
};

const Row = ({ label, value, testid, children }) => (
  <div className="flex items-start justify-between gap-3 text-xs" data-testid={testid}>
    <div className="text-muted-foreground">{label}</div>
    <div className="text-right font-medium">{value || children || <span className="text-muted-foreground">—</span>}</div>
  </div>
);

const computeTimeline = ({ booking_state, proposed_slot, confirmed_slot, calendar_sync_status, calendar_event_id }) => {
  const failed = booking_state === 'calendar_sync_failed' || calendar_sync_status === 'failed';
  const steps = [
    { key: 'request',   label: 'Request',   done: true },
    { key: 'proposed',  label: 'Proposed',  done: !!proposed_slot || !!confirmed_slot || !!calendar_event_id || booking_state === 'booked' },
    { key: 'confirmed', label: 'Confirmed', done: !!confirmed_slot || !!calendar_event_id || booking_state === 'booked' },
    { key: 'synced',    label: 'Synced',    done: calendar_sync_status === 'synced' || (!!calendar_event_id && booking_state === 'booked') },
  ];
  // Failure state: mark last reached step as failed and stop.
  if (failed) {
    const lastDoneIdx = Math.max(0, steps.map(s => s.done).lastIndexOf(true));
    steps.forEach((s, i) => {
      s.failed = false;
      if (i > lastDoneIdx) s.done = false;
    });
    const nextIdx = Math.min(lastDoneIdx + 1, steps.length - 1);
    steps[nextIdx].failed = true;
  }
  return steps;
};

const Timeline = ({ conv }) => {
  const steps = computeTimeline(conv || {});
  return (
    <div
      className="flex items-center gap-1 text-[10px] font-medium overflow-x-auto"
      data-testid="booking-timeline"
    >
      {steps.map((s, i) => (
        <React.Fragment key={s.key}>
          <div
            className={`flex items-center gap-1 px-2 py-1 rounded ${
              s.failed
                ? 'bg-red-50 text-red-700 border border-red-200'
                : s.done
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-muted/40 text-muted-foreground border'
            }`}
            data-testid={`timeline-step-${s.key}-${s.failed ? 'failed' : s.done ? 'done' : 'pending'}`}
          >
            {s.failed
              ? <AlertTriangle className="h-3 w-3" />
              : s.done
                ? <CheckCircle className="h-3 w-3" />
                : <Circle className="h-3 w-3" />}
            <span>{s.label}</span>
          </div>
          {i < steps.length - 1 && <ArrowRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />}
        </React.Fragment>
      ))}
    </div>
  );
};

const BookingStatusBlock = ({ conv }) => {
  if (!conv) return null;
  const {
    booking_state,
    booking_confirmed,
    proposed_slot,
    confirmed_slot,
    calendar_sync_status,
    calendar_sync_provider,
    calendar_sync_is_preview,
    calendar_sync_reason,
    calendar_event_id,
    calendar_event_link,
    is_demo,
  } = conv;

  const hasAny =
    booking_state ||
    proposed_slot ||
    calendar_event_id ||
    confirmed_slot ||
    calendar_sync_status;
  if (!hasAny) return null;

  const meta = STATE_META[booking_state] || STATE_META.collecting;
  const StateIcon = meta.icon;
  const isPreview = calendar_sync_is_preview === true || !!is_demo;
  const isLive = calendar_sync_status === 'synced' && !isPreview;
  const syncMeta = SYNC_META[calendar_sync_status];
  const SyncIcon = syncMeta?.icon;

  const proposedStr = formatSlot(proposed_slot);
  const confirmedStr = formatSlot(confirmed_slot);

  return (
    <div className="border rounded-lg p-3 space-y-3 bg-card" data-testid="booking-status-block">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Calendar className="h-4 w-4 text-blue-600" />
          Booking
        </div>
        {is_demo ? (
          <Badge className="bg-amber-100 text-amber-800 border border-amber-300" data-testid="booking-demo-badge">
            <FlaskConical className="h-3 w-3 mr-1" /> Demo
          </Badge>
        ) : isPreview ? (
          <Badge className="bg-amber-100 text-amber-800 border border-amber-300" data-testid="booking-preview-badge">
            <Eye className="h-3 w-3 mr-1" /> Preview
          </Badge>
        ) : isLive ? (
          <Badge className="bg-emerald-600" data-testid="booking-live-badge">
            <Zap className="h-3 w-3 mr-1" /> Live
          </Badge>
        ) : null}
      </div>

      <Timeline conv={conv} />

      <div className="space-y-2 divide-y">
        <Row
          label="Booking State"
          testid={`booking-state-row-${booking_state || 'unknown'}`}
        >
          <Badge
            className={`${meta.color} border`}
            data-testid={`booking-state-${booking_state || 'unknown'}`}
          >
            <StateIcon className="h-3 w-3 mr-1" /> {meta.label}
          </Badge>
        </Row>

        <Row
          label="Calendar Sync"
          testid="calendar-sync-row"
        >
          {syncMeta ? (
            <Badge className={`${syncMeta.color} border`} data-testid={`calendar-sync-${calendar_sync_status}`}>
              {SyncIcon && <SyncIcon className="h-3 w-3 mr-1" />}
              {syncMeta.label}
            </Badge>
          ) : (
            <span className="text-muted-foreground">not started</span>
          )}
        </Row>

        <Row
          label="Proposed Slot"
          value={proposedStr}
          testid="proposed-slot-row"
        />

        <Row
          label="Confirmed Slot"
          value={confirmedStr}
          testid="confirmed-slot-row"
        />
      </div>

      {calendar_sync_reason && booking_state === 'calendar_sync_failed' && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="booking-sync-failure-reason">
          <b>Sync failed:</b> {calendar_sync_reason}
        </div>
      )}

      {calendar_event_id && (
        <div className="text-xs space-y-1 pt-2 border-t">
          <div className="text-muted-foreground">Calendar event</div>
          <div className="flex items-center gap-2">
            <code className="text-[11px] bg-muted px-1.5 py-0.5 rounded" data-testid="calendar-event-id">{calendar_event_id}</code>
            {calendar_event_link && (
              <a href={calendar_event_link} target="_blank" rel="noopener noreferrer"
                 className="text-blue-600 hover:underline inline-flex items-center gap-0.5"
                 data-testid="calendar-event-link">
                Open <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
          {calendar_sync_provider && (
            <div className="text-muted-foreground">via {calendar_sync_provider}</div>
          )}
        </div>
      )}

      {booking_confirmed && !calendar_event_id && (
        <div className="text-xs text-muted-foreground">Booking confirmed (no calendar event written)</div>
      )}

      {is_demo && (
        <div
          className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 leading-snug"
          data-testid="booking-demo-hint"
        >
          <FlaskConical className="inline h-3 w-3 mr-1" />
          Demo tip: switch the channel's Booking Mode on the Channels page to see how this conversation would play out differently.
        </div>
      )}
    </div>
  );
};

export default BookingStatusBlock;
